import { useEffect, useState, type JSX } from 'react'
import { api } from '../../api/client.js'
import { useToast } from '../../components/system/ToastProvider.js'
import { useAuthStore } from '../../store/authStore.js'
import { Button } from 'primereact/button'
import { InputSwitch } from 'primereact/inputswitch'
import { Dropdown } from 'primereact/dropdown'
import { InputNumber } from 'primereact/inputnumber'
import { InputText } from 'primereact/inputtext'
import RequirePermission from '../../components/auth/RequirePermission.js'
import { Mic, ShieldCheck, Save, RefreshCw } from 'lucide-react'

export interface TenantRecordingConfigData {
  mode: 'all' | 'inbound' | 'outbound' | 'queue' | 'disabled'
  format: 'wav' | 'mp3' | 'opus'
  dualChannel: boolean
  retentionDays: number
  allowAgentPause: boolean
  announcementEnabled: boolean
  announcementMessage: string | null
}

const DEFAULT_CONFIG: TenantRecordingConfigData = {
  mode: 'all',
  format: 'wav',
  dualChannel: true,
  retentionDays: 90,
  allowAgentPause: false,
  announcementEnabled: false,
  announcementMessage: null,
}

const MODE_OPTIONS = [
  { label: 'Record All Calls', value: 'all' },
  { label: 'Inbound Calls Only', value: 'inbound' },
  { label: 'Outbound Calls Only', value: 'outbound' },
  { label: 'Queue Calls Only', value: 'queue' },
  { label: 'Disabled (No Recording)', value: 'disabled' },
]

const FORMAT_OPTIONS = [
  { label: 'WAV (Uncompressed / High Quality)', value: 'wav' },
  { label: 'MP3 (Standard Compression)', value: 'mp3' },
  { label: 'Opus (Modern / WebRTC Optimized)', value: 'opus' },
]

export default function RecordingSettingsPage(): JSX.Element {
  const toast = useToast()
  const user = useAuthStore((s) => s.user)
  const setSession = useAuthStore((s) => s.setSession)
  const csrfToken = useAuthStore((s) => s.csrfToken)

  const [config, setConfig] = useState<TenantRecordingConfigData>(DEFAULT_CONFIG)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const loadConfig = async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await api.get<{ recordingConfig: TenantRecordingConfigData }>('/admin/recording-config')
      if (res?.recordingConfig) {
        setConfig({
          mode: res.recordingConfig.mode || 'all',
          format: res.recordingConfig.format || 'wav',
          dualChannel: res.recordingConfig.dualChannel ?? true,
          retentionDays: res.recordingConfig.retentionDays ?? 90,
          allowAgentPause: res.recordingConfig.allowAgentPause ?? false,
          announcementEnabled: res.recordingConfig.announcementEnabled ?? false,
          announcementMessage: res.recordingConfig.announcementMessage ?? null,
        })
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to load recording configuration'
      setError(msg)
      toast.error(msg)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void loadConfig()
  }, [])

  const handleSave = async () => {
    setSaving(true)
    setError(null)
    try {
      const res = await api.put<{ recordingConfig: TenantRecordingConfigData }>('/admin/recording-config', config)
      if (res?.recordingConfig) {
        setConfig(res.recordingConfig)
        // Keep in-memory user permission in sync
        if (user) {
          setSession({
            user: {
              ...user,
              allowAgentPause: res.recordingConfig.allowAgentPause,
            },
            csrfToken: csrfToken ?? '',
          })
        }
      }
      toast.success('Tenant call recording configuration has been updated successfully.')
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to save recording configuration'
      setError(msg)
      toast.error(msg)
    } finally {
      setSaving(false)
    }
  }

  return (
    <RequirePermission permission="admin:recordings:view">
      <div className="mx-auto max-w-5xl px-4 py-6">
        {/* Header */}
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4 border-b border-slate-200 pb-4">
          <div>
            <div className="flex items-center gap-2">
              <Mic className="h-6 w-6 text-blue-600" />
              <h1 className="text-xl font-bold tracking-tight text-slate-900">Recording Settings</h1>
            </div>
            <p className="mt-1 text-sm text-slate-500">
              Configure tenant-wide call recording policies, storage options, and agent pause/resume controls.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              icon={<RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />}
              label="Reload"
              className="p-button-outlined p-button-secondary text-sm"
              onClick={() => void loadConfig()}
              disabled={loading || saving}
            />
            <Button
              type="button"
              icon={<Save className="mr-2 h-4 w-4" />}
              label={saving ? 'Saving...' : 'Save Settings'}
              className="bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
              onClick={() => void handleSave()}
              disabled={loading || saving}
            />
          </div>
        </div>

        {error && (
          <div className="mb-6 rounded-[3px] border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex h-64 items-center justify-center rounded-[3px] border border-slate-200 bg-white">
            <div className="flex items-center gap-3 text-slate-500">
              <RefreshCw className="h-5 w-5 animate-spin text-blue-600" />
              <span>Loading recording configuration...</span>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Agent Pause / Resume Permission Card */}
            <div className="rounded-[3px] border border-blue-200 bg-blue-50/40 p-5 shadow-sm">
              <div className="flex items-start justify-between gap-4">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <ShieldCheck className="h-5 w-5 text-blue-600" />
                    <span className="text-base font-semibold text-slate-900">
                      Allow Agents to Pause/Resume Call Recording
                    </span>
                  </div>
                  <p className="text-sm text-slate-600">
                    When enabled, agents can temporarily pause and resume call recording during an active call.
                  </p>
                  <p className="text-xs text-slate-500">
                    When disabled, Pause/Resume controls are hidden from agent interfaces and backend requests are rejected.
                  </p>
                </div>
                <div className="pt-1">
                  <InputSwitch
                    checked={config.allowAgentPause}
                    onChange={(e) => setConfig((prev) => ({ ...prev, allowAgentPause: Boolean(e.value) }))}
                    aria-label="Allow Agents to Pause/Resume Call Recording"
                  />
                </div>
              </div>
            </div>

            {/* General Recording Policy */}
            <div className="rounded-[3px] border border-slate-200 bg-white p-5 shadow-sm">
              <h2 className="mb-4 text-sm font-bold uppercase tracking-wider text-slate-700">
                Recording Policy & Capture Mode
              </h2>
              <div className="grid gap-6 md:grid-cols-2">
                <div>
                  <label htmlFor="recording-mode" className="mb-1 block text-xs font-semibold text-slate-700">
                    Recording Mode
                  </label>
                  <Dropdown
                    id="recording-mode"
                    value={config.mode}
                    options={MODE_OPTIONS}
                    onChange={(e) => setConfig((prev) => ({ ...prev, mode: e.value }))}
                    className="w-full text-sm"
                  />
                  <p className="mt-1 text-xs text-slate-500">
                    Determines which types of phone calls will be automatically recorded.
                  </p>
                </div>

                <div>
                  <label htmlFor="recording-format" className="mb-1 block text-xs font-semibold text-slate-700">
                    Audio Format
                  </label>
                  <Dropdown
                    id="recording-format"
                    value={config.format}
                    options={FORMAT_OPTIONS}
                    onChange={(e) => setConfig((prev) => ({ ...prev, format: e.value }))}
                    className="w-full text-sm"
                  />
                  <p className="mt-1 text-xs text-slate-500">
                    Audio container and encoding format used for saved recordings.
                  </p>
                </div>

                <div>
                  <label htmlFor="retention-days" className="mb-1 block text-xs font-semibold text-slate-700">
                    Retention Period (Days)
                  </label>
                  <InputNumber
                    id="retention-days"
                    value={config.retentionDays}
                    onValueChange={(e) => setConfig((prev) => ({ ...prev, retentionDays: e.value ?? 90 }))}
                    min={1}
                    max={3650}
                    className="w-full text-sm"
                  />
                  <p className="mt-1 text-xs text-slate-500">
                    Number of days recordings are retained before automated lifecycle cleanup.
                  </p>
                </div>

                <div className="flex flex-col justify-center">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="block text-xs font-semibold text-slate-700">Dual Channel Recording</span>
                      <p className="text-xs text-slate-500">
                        Record agent on channel 0 and customer on channel 1 for enhanced transcription.
                      </p>
                    </div>
                    <InputSwitch
                      checked={config.dualChannel}
                      onChange={(e) => setConfig((prev) => ({ ...prev, dualChannel: Boolean(e.value) }))}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Compliance & Announcements */}
            <div className="rounded-[3px] border border-slate-200 bg-white p-5 shadow-sm">
              <h2 className="mb-4 text-sm font-bold uppercase tracking-wider text-slate-700">
                Call Announcements
              </h2>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="block text-xs font-semibold text-slate-700">
                      Play Recording Announcement
                    </span>
                    <p className="text-xs text-slate-500">
                      Play a notice to callers informing them that the call may be recorded.
                    </p>
                  </div>
                  <InputSwitch
                    checked={config.announcementEnabled}
                    onChange={(e) => setConfig((prev) => ({ ...prev, announcementEnabled: Boolean(e.value) }))}
                  />
                </div>

                {config.announcementEnabled && (
                  <div>
                    <label htmlFor="announcement-msg" className="mb-1 block text-xs font-semibold text-slate-700">
                      Custom Announcement Message
                    </label>
                    <InputText
                      id="announcement-msg"
                      value={config.announcementMessage ?? ''}
                      onChange={(e) => setConfig((prev) => ({ ...prev, announcementMessage: e.target.value }))}
                      placeholder="This call may be recorded for quality and training purposes."
                      className="w-full text-sm"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </RequirePermission>
  )
}
